import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { driver, Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { tourSteps, tourConfig, TOUR_VERSION } from '@/config/tourConfig';
import { useAuth } from '@/contexts/AuthContext';
import { trackEvent } from '@/services/analyticsService';
import { supabase } from '@/integrations/supabase/client';

interface TourStorageData {
  completed: boolean;
  version: string;
  completedAt: string;
}

// State machine for tour status
type TourStatus = 'idle' | 'running' | 'completed';

interface TourContextType {
  tourStatus: TourStatus;
  isTourRunning: boolean;
  hasTourCompleted: boolean;
  currentStep: number;
  totalSteps: number;
  startTour: () => void;
  endTour: () => void;
  resetTour: () => void;
  skipTour: () => Promise<void>;
}

// User-specific storage key function
const getStorageKey = (userId: string | undefined) => {
  if (!userId) return null;
  return `fork_it_tour_completed_${userId}`;
};

const TourContext = createContext<TourContextType | undefined>(undefined);

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, profile } = useAuth();
  const [tourStatus, setTourStatus] = useState<TourStatus>('idle');
  const [currentStep, setCurrentStep] = useState(0);
  const driverRef = useRef<Driver | null>(null);
  const lastUserIdRef = useRef<string | null>(null);

  // Check localStorage and database on mount and when user ID changes
  useEffect(() => {
    const currentUserId = user?.id ?? null;
    
    console.log('[Tour] useEffect triggered. Checking state:', {
      currentUserId: currentUserId ? `${currentUserId.substring(0, 8)}...` : null,
      lastUserId: lastUserIdRef.current ? `${lastUserIdRef.current.substring(0, 8)}...` : null,
      currentTourStatus: tourStatus,
      hasCompletedOnboarding: profile?.has_completed_onboarding,
    });
    
    // Skip if user ID hasn't changed
    if (currentUserId === lastUserIdRef.current) {
      console.log('[Tour] User ID unchanged. Skipping check.');
      return;
    }
    
    lastUserIdRef.current = currentUserId;
    
    if (!currentUserId) {
      // Reset for guests or on logout
      console.log('[Tour] No user ID. Setting status to idle.');
      setTourStatus('idle');
      return;
    }

    // PRIORITY 1: Check database flag first (for logged-in users)
    if (profile?.has_completed_onboarding === true) {
      console.log('[Tour] Decision: COMPLETED (database flag is true). Setting status to completed.');
      setTourStatus('completed');
      return;
    }

    // PRIORITY 2: Fall back to localStorage
    const userStorageKey = getStorageKey(currentUserId);
    if (!userStorageKey) {
      console.log('[Tour] No storage key generated. This should not happen.');
      return;
    }

    const stored = localStorage.getItem(userStorageKey);
    console.log(`[Tour] Checking localStorage. Key: ${userStorageKey}, Found: ${stored}`);

    if (stored) {
      try {
        const data: TourStorageData = JSON.parse(stored);
        console.log('[Tour] Parsed localStorage data:', data);
        
        if (data.completed && data.version === TOUR_VERSION) {
          console.log(`[Tour] Decision: COMPLETED (version match). Setting status to 'completed'.`);
          setTourStatus('completed');
        } else {
          console.log(`[Tour] Decision: OUTDATED (version mismatch or not completed). Setting status to 'idle'. Stored version: ${data.version}, Current: ${TOUR_VERSION}`);
          setTourStatus('idle');
        }
      } catch (e) {
        console.error('[Tour] Failed to parse localStorage data. Removing invalid entry.', e);
        localStorage.removeItem(userStorageKey);
        setTourStatus('idle');
      }
    } else {
      console.log(`[Tour] Decision: NOT FOUND in localStorage. Setting status to 'idle'.`);
      setTourStatus('idle');
    }
  }, [user?.id, profile?.has_completed_onboarding]);

  const saveTourCompleted = useCallback(async (alsoPersistToDatabase: boolean = false) => {
    const userStorageKey = getStorageKey(user?.id);
    if (!userStorageKey) {
      console.log('[Tour] saveTourCompleted: No storage key (no user). Skipping save.');
      return;
    }

    // Save to localStorage
    const data: TourStorageData = {
      completed: true,
      version: TOUR_VERSION,
      completedAt: new Date().toISOString(),
    };
    
    console.log(`[Tour] SAVING completion. Key: ${userStorageKey}, Value: ${JSON.stringify(data)}`);
    localStorage.setItem(userStorageKey, JSON.stringify(data));
    
    // Verify save was successful
    const verifyRead = localStorage.getItem(userStorageKey);
    console.log(`[Tour] Save verification. Read back: ${verifyRead}`);

    // Also persist to database if requested
    if (alsoPersistToDatabase && user?.id) {
      console.log('[Tour] Persisting completion to database...');
      const { error } = await supabase
        .from('profiles')
        .update({ has_completed_onboarding: true })
        .eq('id', user.id);
      
      if (error) {
        console.error('[Tour] Failed to persist to database:', error);
      } else {
        console.log('[Tour] Successfully persisted to database.');
      }
    }
  }, [user?.id]);

  // Skip tour function - immediately ends tour and saves to both localStorage AND database
  const skipTour = useCallback(async () => {
    console.log('[Tour] skipTour called - user explicitly skipped the tour');
    
    // Immediately destroy the driver if running
    if (driverRef.current) {
      driverRef.current.destroy();
      driverRef.current = null;
    }
    
    // Save to both localStorage AND database
    await saveTourCompleted(true);
    
    // Update state
    setTourStatus('completed');
    setCurrentStep(0);
    
    // Track analytics
    trackEvent('Onboarding', 'Skip Tour', `Version ${TOUR_VERSION}`);
  }, [saveTourCompleted]);

  const startTour = useCallback(() => {
    console.log('[Tour] startTour called. Current status:', tourStatus);
    
    // State machine guard: Only start from 'idle' state
    if (tourStatus !== 'idle') {
      console.log(`[Tour] Aborting start. Status is '${tourStatus}', not 'idle'.`);
      return;
    }

    // Additional synchronous localStorage check as a safety net
    const userStorageKey = getStorageKey(user?.id);
    if (userStorageKey) {
      const stored = localStorage.getItem(userStorageKey);
      if (stored) {
        try {
          const data: TourStorageData = JSON.parse(stored);
          if (data.completed && data.version === TOUR_VERSION) {
            console.log('[Tour] Guard: Blocking start - localStorage shows already completed.');
            setTourStatus('completed');
            return;
          }
        } catch (e) {
          // Ignore parsing errors
        }
      }
    }

    console.log('[Tour] Setting status to "running" and initializing driver...');
    setTourStatus('running');

    // Initialize driver with custom onNextClick for first step
    const driverInstance = driver({
      ...tourConfig,
      steps: tourSteps,
      onHighlightStarted: (element, step) => {
        setCurrentStep(tourSteps.indexOf(step as any));
      },
      onNextClick: () => {
        if (driverInstance.isLastStep()) {
          driverInstance.destroy();
        } else {
          driverInstance.moveNext();
        }
      },
      onCloseClick: () => {
        driverInstance.destroy();
      },
      onDestroyStarted: () => {
        // Save completion immediately when destruction begins
        saveTourCompleted(true);
        setCurrentStep(0);
      },
      onDestroyed: () => {
        console.log('[Tour] onDestroyed: Tour is now complete. Status -> completed.');
        // Track analytics event for tour completion
        trackEvent('Onboarding', 'Complete Tour', `Version ${TOUR_VERSION}`);
        setTourStatus('completed');
        driverRef.current = null;
      },
    });

    driverRef.current = driverInstance;
    driverInstance.drive();
  }, [tourStatus, saveTourCompleted, user?.id]);

  const endTour = useCallback(() => {
    if (driverRef.current) {
      driverRef.current.destroy();
    }
    saveTourCompleted(true);
    setTourStatus('completed');
    setCurrentStep(0);
  }, [saveTourCompleted]);

  const resetTour = useCallback(() => {
    const userStorageKey = getStorageKey(user?.id);
    if (userStorageKey) {
      localStorage.removeItem(userStorageKey);
    }
    setTourStatus('idle');
    setCurrentStep(0);
  }, [user?.id]);

  // Listen for skip request from tour checkbox
  useEffect(() => {
    const handleSkipRequest = () => {
      console.log('[Tour] Skip request received from checkbox');
      skipTour();
    };
    
    window.addEventListener('tour-skip-requested', handleSkipRequest);
    return () => window.removeEventListener('tour-skip-requested', handleSkipRequest);
  }, [skipTour]);

  // Cleanup effect - runs ONLY on true component unmount
  useEffect(() => {
    return () => {
      if (driverRef.current) {
        console.log('[Tour] Component unmounting. Destroying active driver instance.');
        driverRef.current.destroy();
      }
    };
  }, []);

  const value: TourContextType = {
    tourStatus,
    isTourRunning: tourStatus === 'running',
    hasTourCompleted: tourStatus === 'completed',
    currentStep,
    totalSteps: tourSteps.length,
    startTour,
    endTour,
    resetTour,
    skipTour,
  };

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  );
};

export const useTour = (): TourContextType => {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};
